import {
  PriceStyleVariant,
  Variant,
  VolumeDiscountThreshold,
} from '@app/components/Price/Price';
import {
  ProcessedPrice,
  ProcessedVolumeDiscount,
  ProcessedVolumeDiscountThreshold,
} from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import { SimilarDesignInventoryItem } from '@app/data/clients/rbServerSideClient/queries/GetSimilarDesigns/types';
import { Shop_Inventory_VolumeDiscount } from '@app/data/shop-bff';
import type { DiscountType } from '@redbubble/next-analytics/gtm';
import { round } from 'lodash';
import { InventoryItem } from 'types/shop-bff/types';
import { Price } from '../components/Price';

export const getDiscountTypeAndAmount = (
  thresholds: VolumeDiscountThreshold[] | ProcessedVolumeDiscountThreshold[],
  price?: Price | ProcessedPrice | null
): {
  discountType: DiscountType;
  variant: Variant;
  discountedAmount: number;
  originalAmount: number;
} | null => {
  if (!price) {
    return null;
  }

  // Always show discounted (strikethrough) price if available
  if (price.discount?.amount > 0) {
    return {
      discountType: 'promotion',
      variant: 'defaultPriceStyle',
      discountedAmount: price.discount.amount,
      originalAmount: price.amount,
    };
  }

  // Next show volume bundle discounted price if available
  if (thresholds.length > 0) {
    const maximumVolumeDiscountPercentageOff = Math.max(
      ...thresholds.map(({ percentOff }) => percentOff),
      0
    );
    const fromAmount = round(
      (price.amount * (100 - maximumVolumeDiscountPercentageOff)) / 100,
      2
    );
    return {
      discountType: 'volume',
      variant: 'fromPriceStyle',
      discountedAmount: fromAmount,
      originalAmount: price.amount,
    };
  }

  // Fallback to returning the normal prices
  return {
    discountType: 'none',
    variant: 'defaultPriceStyle',
    discountedAmount: price.amount,
    originalAmount: price.amount,
  };
};

export const getPriceComponent = (
  thresholds:
    | Shop_Inventory_VolumeDiscount['thresholds']
    | ProcessedVolumeDiscount['thresholds']
    | NonNullable<SimilarDesignInventoryItem['volumeDiscount']>['thresholds'],
  price:
    | InventoryItem['price']
    | ProcessedPrice
    | NonNullable<SimilarDesignInventoryItem['price']>,
  styleVariant?: PriceStyleVariant
): React.ReactNode => {
  if (!price) {
    return null;
  }

  const priceTypeAndAmount = getDiscountTypeAndAmount(thresholds, price);
  if (!priceTypeAndAmount) {
    return null;
  }

  const { discountType, originalAmount, discountedAmount } = priceTypeAndAmount;

  switch (discountType) {
    // Discount calculations happen in Price component for custom discount
    case 'promotion':
      return (
        <Price
          size="small"
          discount={price.discount}
          amount={originalAmount}
          currency={price.currency}
          hideVATOverride
          styleVariant={styleVariant}
        />
      );

    case 'volume':
      return (
        <Price
          variant="fromPriceStyle"
          size="small"
          amount={discountedAmount}
          currency={price.currency}
          hideVATOverride
          styleVariant={styleVariant}
        />
      );

    case 'none':
      return (
        <Price
          size="small"
          amount={originalAmount}
          currency={price.currency}
          hideVATOverride
          styleVariant={styleVariant}
        />
      );

    default:
      return null;
  }
};
